import { TRANSACTION_KIND } from '@/constants/wallet';
import { resolveLedgerTitle } from '@/utils/transactionDisplay.js';

// Pure helpers for the carton account (`cartonAccount` on the order box — the same
// numbers the invoice PDF prints). The contract is the backend DTO, read it there:
// ../backend/app/DataObjects/Commerce/CartonAccount/*.php. Nothing here recomputes
// money; it only orders, words and dates what the backend already decided.

export const CARTON_STATE = Object.freeze({
  PENDING: 'pending',
  PAID: 'paid',
  DUE: 'due',
});

// Western digits always (owner-locked UI rule); `-u-nu-latn` guarantees it whatever
// the engine's default numbering system for Arabic is.
const DATE_LOCALE = Object.freeze({ ar: 'ar-u-nu-latn', en: 'en-u-nu-latn' });

/**
 * «25 سبتمبر» (or «الخميس 25 سبتمبر» with `weekday`). Null for a missing/invalid date.
 * @param {string|null|undefined} iso
 * @param {string} [locale]
 * @param {{weekday?: boolean}} [opts]
 * @returns {string|null}
 */
export function formatCartonDate(iso, locale = 'ar', { weekday = false } = {}) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(DATE_LOCALE[locale] ?? DATE_LOCALE.ar, {
    day: 'numeric',
    month: 'long',
    ...(weekday ? { weekday: 'long' } : {}),
  }).format(date);
}

/**
 * The shop groups, in the backend's order (largest first) except that the group of
 * lines with no shop — manual lines — always closes the list.
 * @param {object|null} account  cartonAccount
 * @returns {Array<object>}
 */
export function orderedItemGroups(account) {
  const groups = Array.isArray(account?.items?.groups) ? account.items.groups : [];
  const shops = groups.filter((group) => group?.supplierId != null);
  const manual = groups.filter((group) => group?.supplierId == null);
  return [...shops, ...manual];
}

/**
 * Club support rate as a display number: 500 bp → 5, 250 bp → 2.5.
 * @param {number|null|undefined} bp
 * @returns {number}
 */
export function basisPointsToPct(bp) {
  return Number(((Number(bp) || 0) / 100).toFixed(2));
}

// Row kinds whose carton-account title uses the PDF's noun (invoice.php rows.*) rather
// than the wallet page's shorter label: in a list of this carton's movements «فاتورة»
// alone or «حجز {shop}» does not say what happened.
const CARTON_ROW_KEYS = Object.freeze({
  [TRANSACTION_KIND.INVOICE_HOLD]: { param: 'carton', field: 'carton' },
  [TRANSACTION_KIND.SUPPLIER_DIRECT_DEPOSIT]: { param: 'supplier', field: 'supplier' },
  [TRANSACTION_KIND.POOLCREDIT_EARNING]: { param: 'supplier', field: 'supplier' },
});

/**
 * Words a carton-account wallet row. The row's `title` is the backend's WalletRowTitle
 * — a rule key plus the data it needs, never Arabic — and it mirrors resolveLedgerTitle
 * (the wallet page's rule), so everything but the carton-specific kinds above is handed
 * straight to it and reads exactly as on the wallet page.
 * @param {object} title  {key, kind, reason, account, supplier, carton, item, itemQty}
 * @param {(key: string, named?: object) => string} t
 * @param {(key: string) => boolean} te
 * @returns {string}
 */
export function cartonRowTitle(title, t, te) {
  const safe = title || {};
  const carton = CARTON_ROW_KEYS[safe.key];
  if (carton) {
    const value = typeof safe[carton.field] === 'string' ? safe[carton.field].trim() : '';
    return value
      ? t(`box.totals.account.rows.${safe.key}`, { [carton.param]: value })
      : t(`box.totals.account.rows.${safe.key}_unnamed`);
  }
  return resolveLedgerTitle({
    kind: safe.kind,
    supplierName: safe.supplier,
    reason: safe.reason,
    paymentAccountLabel: safe.account,
    method: safe.key === 'deposit_cash' ? 'cash' : null,
    orderBoxItemName: safe.item,
    orderBoxItemQty: safe.itemQty,
    t,
    te,
  });
}

const ARABIC_LETTER = /[؀-ۿ]/;

/**
 * The Arabic part of a PoolShare program's stored name. Names are stored bilingual
 * («Everyday Earning · الكسب الدائم»); the page shows the Arabic half, exactly as the
 * PDF does. Null when the name has none — the caller says plain «بولشير».
 * @param {string|null|undefined} title
 * @returns {string|null}
 */
export function programArabicName(title) {
  if (typeof title !== 'string') return null;
  const part = title
    .split(/\s*[·|/]\s*/)
    .map((piece) => piece.trim())
    .find((piece) => ARABIC_LETTER.test(piece));
  return part || null;
}
